"use client";

import { Check, Loader2, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "@/studio/components/studio-confirm-overlay.css";
import "./payment-processing-overlay.css";

/** Confirmed button: "Sign in" when paid during sign-up, "View invoice" inside Studio. */
export type PaymentConfirmedLabel = "Sign in" | "View invoice";

export type PaymentOverlayView =
  | { phase: "processing" }
  | { phase: "success"; detail?: string; actionLabel?: PaymentConfirmedLabel }
  | { phase: "error"; message: string; actionLabel?: "Try again" | "Close" };

/**
 * Full-screen blur veil over checkout. Processing: spinner, blocks clicks, warns before unload.
 * Success / error: same layout as Wam's PaymentReceivedOverlay (check in a circle, title, line, button)
 * and stays until the button is clicked.
 */
export function PaymentProcessingOverlay({
  view,
  onDismiss,
}: {
  view: PaymentOverlayView | null;
  onDismiss?: () => void;
}) {
  const [mounted, setMounted] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => setMounted(true), []);
  const phase = view?.phase ?? null;

  useEffect(() => {
    if (phase !== "processing") return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [phase]);

  useEffect(() => {
    if (phase === "success" || phase === "error") buttonRef.current?.focus();
  }, [phase]);

  if (!view || !mounted) return null;
  const swallow = (event: { target: EventTarget; currentTarget: EventTarget; stopPropagation: () => void; preventDefault: () => void }) => {
    // Clicks on the veil never reach the page; the real button still works.
    event.stopPropagation();
    if (event.target === event.currentTarget || view.phase === "processing") event.preventDefault();
  };
  const titleId = "studio-payment-processing-title";
  const copyId = "studio-payment-processing-copy";
  return createPortal(
    <div
      className={`studio-payment-celebration studio-payment-processing is-${view.phase}`}
      role={view.phase === "processing" ? "alertdialog" : "dialog"}
      aria-modal="true"
      aria-busy={view.phase === "processing"}
      aria-labelledby={titleId}
      aria-describedby={copyId}
      data-payment-processing=""
      data-no-press=""
      tabIndex={-1}
      onClick={swallow}
      onMouseDown={swallow}
      onPointerDown={swallow}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          event.preventDefault();
        }
      }}
      style={{ zIndex: 2147483000, pointerEvents: "auto" }}
    >
      <div className="studio-payment-celebration-inner">
        {view.phase === "processing" ? (
          <>
            <Loader2 className="studio-payment-celebration-spin" aria-hidden="true" />
            <h2 id={titleId} className="studio-payment-celebration-title">Confirming your payment</h2>
            <p id={copyId} className="studio-payment-celebration-copy">
              Please don&apos;t close or refresh this window.
            </p>
          </>
        ) : view.phase === "success" ? (
          <>
            <span className="studio-payment-celebration-check is-filled" aria-hidden="true"><Check strokeWidth={3} /></span>
            <h2 id={titleId} className="studio-payment-celebration-title">Payment confirmed</h2>
            <p id={copyId} className="studio-payment-celebration-copy">
              {view.detail || "Your payment went through."}
            </p>
            <div className="studio-payment-celebration-actions">
              <button ref={buttonRef} type="button" className="studio-payment-celebration-btn" onClick={onDismiss}>
                {view.actionLabel ?? "View invoice"}
              </button>
            </div>
          </>
        ) : (
          <>
            <XCircle className="studio-payment-celebration-check is-danger" aria-hidden="true" strokeWidth={1.75} />
            <h2 id={titleId} className="studio-payment-celebration-title">Payment didn&apos;t go through</h2>
            <p id={copyId} className="studio-payment-celebration-copy">{view.message}</p>
            <div className="studio-payment-celebration-actions">
              <button ref={buttonRef} type="button" className="studio-payment-celebration-btn" onClick={onDismiss}>
                {view.actionLabel ?? "Try again"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
